import React, { useEffect, useState } from 'react';
import { subMonths } from 'date-fns';
import { Button, Message, Popup, Table } from 'semantic-ui-react';
import Loader from '../../../../../components/loader';
import RangeDatePicker from '../../../../../components/rangeDatePicker';
import { RESPONSE_STATUS } from '../../../../../constnats/general';
import { ROLES } from '../../../../../constnats/user';
import useDateRange from '../../../../../hooks/useDateRange';
import type { QaReviewKpi } from '../../../../../schemas/qaReviewKpis.schemas';
import type { UserType } from '../../../../../types/user.type';
import TableUpdatesRepo from '../../../../../utils/repository/tableUpdatesRepo';
import type { QaReviewKpisResponseSchemaType } from '../../../../../utils/repository/tableUpdatesRepo/schemas';
import UserRepo from '../../../../../utils/repository/userRepo';
import { formatQaReviewPercentage, getQaReviewMatrix } from '../../utils/qaReviews';
import './qaReviews.css';

type LoadState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'loaded'; reviews: QaReviewKpi[]; users: UserType[] };

export default function QaReviews() {
    const range = useDateRange({ from: subMonths(new Date(), 1), to: new Date() });
    const from = new Date(range.from.getTime()).setUTCHours(0, 0, 0, 0);
    const to = new Date(range.to.getTime()).setUTCHours(23, 59, 59, 999);
    const [state, setState] = useState<LoadState>({ status: 'loading' });
    const [retry, setRetry] = useState(0);
    const validRange = Number.isFinite(from) && Number.isFinite(to) && from <= to;

    useEffect(() => {
        if (!validRange) {
            return;
        }

        let active = true;
        setState({ status: 'loading' });
        Promise.all([
            new Promise<QaReviewKpisResponseSchemaType>((resolve, reject) => {
                TableUpdatesRepo.qaReviewsByUpdateOwner({ from, to }, resolve, reject);
            }),
            new Promise<UserType[]>((resolve, reject) => {
                UserRepo.list({ userRoles: [ROLES.EMPLOYEE], includeDisabled: true }, resolve, reject);
            }),
        ]).then(([response, users]) => {
            if (!active) {
                return;
            }
            if (response.status !== RESPONSE_STATUS.SUCCESS) {
                setState({ status: 'error' });
                return;
            }
            setState({ status: 'loaded', reviews: response.data, users });
        }, () => {
            if (active) {
                setState({ status: 'error' });
            }
        });

        return () => {
            active = false;
        };
    }, [from, to, validRange, retry]);

    function renderContent() {
        if (!validRange) {
            return <Message warning role='alert'>Choose a date range with From on or before To.</Message>;
        }
        if (state.status === 'loading') {
            return <Loader msg='Loading QA reviews' />;
        }
        if (state.status === 'error') {
            return <Message negative role='alert'>
                <p>Could not load QA reviews. Please try again.</p>
                <Button onClick={() => setRetry(value => value + 1)}>Retry</Button>
            </Message>;
        }

        const matrix = getQaReviewMatrix(state.reviews, state.users);
        if (matrix.reviewers.length === 0) {
            return <Message role='status'>No QA reviews found for this date range.</Message>;
        }

        return <div className='qa-reviews__scroll' role='region' aria-label='QA reviews by update owner' tabIndex={0}>
            <Table striped selectable celled unstackable className='qa-reviews__table' aria-label='QA reviews by update owner'>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell scope='col'>QA reviewer</Table.HeaderCell>
                        {matrix.owners.map(owner => {
                            const [firstName, ...remainingNames] = owner.label.trim().split(/\s+/);

                            return <Table.HeaderCell key={owner.id} scope='col' aria-label={owner.label}>
                                {firstName}
                                {remainingNames.length > 0 && <><br />{remainingNames.join(' ')}</>}
                            </Table.HeaderCell>;
                        })}
                        <Table.HeaderCell scope='col'>Total</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {matrix.reviewers.map(reviewer => <Table.Row key={reviewer.id}>
                        <Table.Cell as='th' scope='row'>{reviewer.label}</Table.Cell>
                        {matrix.owners.map(owner => {
                            const count = reviewer.counts.get(owner.id) ?? 0;
                            const percentage = formatQaReviewPercentage(count, reviewer.total);
                            const countLabel = `${count} ${count === 1 ? 'review' : 'reviews'}`;

                            return <Popup
                                key={owner.id}
                                on={['hover', 'focus']}
                                size='tiny'
                                content={countLabel}
                                trigger={<Table.Cell textAlign='right' tabIndex={0} aria-label={`${percentage}, ${countLabel}`}>
                                    {percentage}
                                </Table.Cell>}
                            />;
                        })}
                        <Table.Cell textAlign='right'><strong>{reviewer.total}</strong></Table.Cell>
                    </Table.Row>)}
                </Table.Body>
            </Table>
        </div>;
    }

    return <>
        <RangeDatePicker {...range} />
        <p>Rows are QA reviewers; columns are update owners. Percentages show each owner's share of the reviewer's total reviews.</p>
        {renderContent()}
    </>;
}
